import { useState } from 'react';
import { Helmet } from 'react-helmet';
import { useHistory } from 'react-router-dom';
import { FormattedMessage, useIntl } from 'react-intl';
import { Button } from 'antd';
import moment from 'moment';
import { ColumnsType } from 'antd/lib/table';

import { PageProps } from '@/types/PageProps';

import { flatInfiniteResult } from '@/utils/request/flatInfiniteResult';

import {
  StockCorrection,
  StockCorrectionsQuery,
} from '@/core/domain/stock/StockCorrections/StockCorrections.types';

import { useStockCorrections } from '@/core/domain/stock/StockCorrections/StockCorrections.hooks';

import AppPageHeader from '@/components/_common/AppPageHeader';
import TabContentWrapper from '@/components/_common/TabContentWrapper';
import InfiniteTable from '@/components/_common/InfiniteTable';
import StockCorrectionsExtraFilters from '@/components/Movements/StockCorrections/StockCorrectionsExtraFilters';
import { getTableScrollX } from '@/utils/table';
import EditIcon from '@/components/_common/EditIcon';
import SpaceForButton from '@/components/_common/SpaceForButtons';
import { useDebounce } from '@/hooks';
import { convertParams, usePersistSearchParams } from '@/utils/queryParams';
import { hasPermissions } from '@/utils/functions/hasPermissions';

const StockCorrectionsPage: React.FC<PageProps> = ({ route }: PageProps) => {
  const intl = useIntl();
  const history = useHistory();

  const [queryParams, setQueryParams] = useState<StockCorrectionsQuery>({
    order: 'DESC',
    ...convertParams<StockCorrectionsQuery>({
      search: 'string',
      date: 'string',
      order: 'string',
    }),
  });

  const queryDebounce = useDebounce(queryParams, 500);
  usePersistSearchParams(queryDebounce);

  const { data, isFetching, hasNextPage, fetchNextPage } =
    useStockCorrections(queryDebounce);

  const stockCorrections = flatInfiniteResult(data);

  const columns: ColumnsType<StockCorrection> = [
    {
      dataIndex: ['company', 'name'],
      title: intl.formatMessage({ id: 'stockCorrections.domain.company' }),
      width: 150,
      ellipsis: true,
    },
    {
      dataIndex: ['employee', 'name'],
      title: intl.formatMessage({ id: 'stockCorrections.domain.employee' }),
      width: 150,
      ellipsis: true,
    },
    {
      dataIndex: 'date',
      title: intl.formatMessage({ id: 'stockCorrections.domain.date' }),
      width: 150,
      render: (row) => moment(row).format('DD/MM/YYYY HH:mm'),
    },
    {
      title: intl.formatMessage({ id: 'commons.list.header.action' }),
      width: 50,
      align: 'right',
      fixed: 'right',
      render(row: StockCorrection) {
        return (
          <EditIcon redirect={`/app/movements/stockCorrections/${row.id}`} />
        );
      },
    },
  ];

  return (
    <>
      <Helmet title={intl.formatMessage({ id: route.name })} />

      <TabContentWrapper
        title={route.name}
        permissions={route.permissions}
        extra={
          <SpaceForButton>
            <Button
              type="primary"
              block
              disabled={!hasPermissions(route.permissions)}
              onClick={() => {
                history.push('/app/movements/stockCorrections/create');
              }}
              id="stock-corretion-create-button"
            >
              <FormattedMessage id="stockCorrections.create" />
            </Button>
          </SpaceForButton>
        }
      >
        <AppPageHeader
          onSearch={(value: string) =>
            setQueryParams((prev) => ({ ...prev, search: value }))
          }
          searchValue={queryParams.search}
          extraFilters={
            <StockCorrectionsExtraFilters
              queryParams={queryParams}
              setQueryParams={setQueryParams}
            />
          }
        />

        <InfiniteTable<StockCorrection>
          size="small"
          columns={columns}
          dataSource={stockCorrections}
          loading={isFetching}
          rowKey={(row) => row.id}
          hasNextPage={hasNextPage}
          fetchNextPage={fetchNextPage}
          scroll={{ x: getTableScrollX(columns) }}
        />
      </TabContentWrapper>
    </>
  );
};

export default StockCorrectionsPage;
